import { Check, ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Footer } from "@/components/footer";
import { Header } from "@/components/header";

interface Villa {
  id: number;
  villa_number: number;
  name: string;
  description: string;
  size_sqm: number;
  max_guests: number;
}

interface PricingBreakdown {
  nights: number;
  breakdown: {
    lowSeasonNights: number;
    lowSeasonRate: number;
    lowSeasonTotal: number;
    highSeasonNights: number;
    highSeasonRate: number;
    highSeasonTotal: number;
  };
  totalAmount: number;
  currency: string;
  isHighSeason: boolean;
  isMixedSeason: boolean;
}

export function BookGuestDetailsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkIn = searchParams.get("checkIn");
  const checkOut = searchParams.get("checkOut");
  const villaId = searchParams.get("villaId");

  const [villa, setVilla] = useState<Villa | null>(null);
  const [pricing, setPricing] = useState<PricingBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reservationComplete, setReservationComplete] = useState(false);
  const [reservationId, setReservationId] = useState<number | null>(null);

  // Form state
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [numberOfGuests, setNumberOfGuests] = useState("2");
  const [specialRequests, setSpecialRequests] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!checkIn || !checkOut || !villaId) {
      navigate("/book");
      return;
    }

    fetchVilla();
    fetchPricing();
  }, [checkIn, checkOut, villaId]);

  const fetchVilla = async () => {
    try {
      const response = await fetch("/api/villas");
      const data = (await response.json()) as { success: boolean; villas: Villa[] };
      if (data.success) {
        const selectedVilla = data.villas.find((v: Villa) => v.id === parseInt(villaId!));
        if (selectedVilla) {
          setVilla(selectedVilla);
        } else {
          navigate("/book");
        }
      }
    } catch (error) {
      console.error("Failed to fetch villa:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `/api/pricing/calculate?checkIn=${checkIn}&checkOut=${checkOut}&villaId=${villaId}`
      );
      const data = (await response.json()) as { success: boolean } & PricingBreakdown;
      if (data.success) {
        setPricing(data);
      }
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/reservations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          villaId: parseInt(villaId!),
          guestName,
          guestEmail,
          guestPhone,
          checkIn,
          checkOut,
          numberOfGuests: parseInt(numberOfGuests),
          specialRequests,
        }),
      });

      const data = (await response.json()) as {
        success: boolean;
        message?: string;
        reservation: { id: number };
      };

      if (data.success) {
        setReservationId(data.reservation.id);
        setReservationComplete(true);
      } else {
        setError(data.message || "Failed to create reservation");
      }
    } catch (err) {
      setError("Failed to create reservation. Please try again.");
      console.error("Reservation error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getVillaImage = (villaNumber: number) => {
    const images = [
      "/images/villas/villa1.jpg",
      "/images/villas/villa2.jpg",
      "/images/villas/villa3.jpg",
      "/images/villas/villa4.jpg",
      "/images/villas/villa5.jpg",
      "/images/villas/villa6.jpg",
      "/images/villas/villa7.jpg",
      "/images/villas/villa1.jpg",
    ];
    return images[(villaNumber - 1) % images.length];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full size-12 border-b-2 border-stone-900" />
            <p className="mt-4 text-stone-600">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (reservationComplete) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Header />
        <main className="flex-1 py-16 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-stone-200 p-8 md:p-12 text-center rounded-lg">
              <div className="inline-flex items-center justify-center size-16 bg-green-100 text-green-700 rounded-full mb-6">
                <Check className="size-8" />
              </div>
              <h1 className="font-['Libre_Baskerville'] text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                Reservation Confirmed!
              </h1>
              <p className="text-lg text-stone-600 mb-6">
                Thank you for booking with The Amor. Your reservation has been confirmed.
              </p>
              <div className="bg-stone-50 p-6 rounded-lg mb-8 text-left">
                <div className="grid gap-4">
                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Reservation Number
                    </div>
                    <div className="font-mono font-semibold text-lg">
                      #{reservationId?.toString().padStart(6, "0")}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Villa
                    </div>
                    <div className="font-semibold">{villa?.name}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                        Check-in
                      </div>
                      <div className="font-semibold">{checkIn ? formatDate(checkIn) : ""}</div>
                    </div>
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                        Check-out
                      </div>
                      <div className="font-semibold">{checkOut ? formatDate(checkOut) : ""}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Total Amount
                    </div>
                    <div className="font-bold text-xl">
                      ${pricing?.totalAmount} {pricing?.currency}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-stone-600 mb-8">
                A confirmation email has been sent to <strong>{guestEmail}</strong>
              </p>
              <button
                onClick={() => navigate("/")}
                className="bg-stone-900 text-white px-8 py-3 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors"
              >
                Return Home
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Header />

      <main className="flex-1 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate(`/book/select-villa?checkIn=${checkIn}&checkOut=${checkOut}`)}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-8 transition-colors"
          >
            <ChevronLeft className="size-5" />
            Choose Different Villa
          </button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-['Libre_Baskerville'] text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Guest Details
            </h1>
            <p className="text-stone-600 text-lg">Complete your reservation for {villa?.name}</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-stone-200 p-6 md:p-8 rounded-lg"
              >
                <h2 className="font-semibold text-xl mb-6">Contact Information</h2>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
                    {error}
                  </div>
                )}

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="guestName" className="block text-sm font-semibold mb-2">
                      Full Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="guestName"
                      required
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="w-full border border-stone-300 px-4 py-3 rounded focus:outline-none focus:border-stone-900"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="guestEmail" className="block text-sm font-semibold mb-2">
                      Email Address <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="guestEmail"
                      required
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      className="w-full border border-stone-300 px-4 py-3 rounded focus:outline-none focus:border-stone-900"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="guestPhone" className="block text-sm font-semibold mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="guestPhone"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="w-full border border-stone-300 px-4 py-3 rounded focus:outline-none focus:border-stone-900"
                      placeholder="+66 123 456 789"
                    />
                  </div>

                  <div>
                    <label htmlFor="numberOfGuests" className="block text-sm font-semibold mb-2">
                      Number of Guests <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="numberOfGuests"
                      required
                      value={numberOfGuests}
                      onChange={(e) => setNumberOfGuests(e.target.value)}
                      className="w-full border border-stone-300 px-4 py-3 rounded focus:outline-none focus:border-stone-900"
                    >
                      {Array.from({ length: villa?.max_guests || 2 }, (_, i) => i + 1).map(
                        (num) => (
                          <option key={num} value={num}>
                            {num} {num === 1 ? "Guest" : "Guests"}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="specialRequests" className="block text-sm font-semibold mb-2">
                      Special Requests
                    </label>
                    <textarea
                      id="specialRequests"
                      value={specialRequests}
                      onChange={(e) => setSpecialRequests(e.target.value)}
                      rows={4}
                      className="w-full border border-stone-300 px-4 py-3 rounded focus:outline-none focus:border-stone-900 resize-none"
                      placeholder="Any special requests or requirements..."
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-stone-900 text-white px-8 py-4 mt-8 text-sm tracking-widest uppercase font-semibold hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Processing..." : "Complete Reservation"}
                </button>

                <p className="text-xs text-stone-500 mt-4 text-center">
                  By completing this reservation, you agree to our terms and conditions
                </p>
              </form>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-stone-200 p-6 rounded-lg sticky top-4">
                <h3 className="font-semibold text-lg mb-4">Booking Summary</h3>

                {villa && (
                  <>
                    <img
                      src={getVillaImage(villa.villa_number)}
                      alt={villa.name}
                      className="w-full h-40 object-cover rounded mb-4"
                    />
                    <h4 className="font-['Libre_Baskerville'] font-bold text-lg mb-4">
                      {villa.name}
                    </h4>
                  </>
                )}

                <div className="space-y-3 text-sm border-t border-stone-200 pt-4">
                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Check-in
                    </div>
                    <div className="font-semibold">{checkIn ? formatDate(checkIn) : ""}</div>
                  </div>

                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Check-out
                    </div>
                    <div className="font-semibold">{checkOut ? formatDate(checkOut) : ""}</div>
                  </div>

                  <div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-1">
                      Duration
                    </div>
                    <div className="font-semibold">
                      {pricing?.nights} {pricing?.nights === 1 ? "night" : "nights"}
                    </div>
                  </div>
                </div>

                {pricing && (
                  <div className="border-t border-stone-200 mt-4 pt-4">
                    <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">
                      Price Details
                    </div>
                    <div className="space-y-2 text-sm">
                      {pricing.breakdown.lowSeasonNights > 0 && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">
                            {pricing.breakdown.lowSeasonNights} × ${pricing.breakdown.lowSeasonRate}
                          </span>
                          <span className="font-semibold">${pricing.breakdown.lowSeasonTotal}</span>
                        </div>
                      )}
                      {pricing.breakdown.highSeasonNights > 0 && (
                        <div className="flex justify-between">
                          <span className="text-stone-600">
                            {pricing.breakdown.highSeasonNights} × $
                            {pricing.breakdown.highSeasonRate}
                          </span>
                          <span className="font-semibold">
                            ${pricing.breakdown.highSeasonTotal}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-stone-300 mt-4 pt-4 flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>
                        ${pricing.totalAmount} {pricing.currency}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
